import { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { ReactSortable } from 'react-sortablejs';
import { notifier } from 'components/Toaster';
import useGetExamTypes from 'hooks/API/GET/useGetExamTypes';
import useGetExamBoards from 'hooks/API/GET/useGetExamBoards';
import {
  useDeleteExamType,
  useManageExamType,
  useReorderExamTypes,
} from 'hooks/API/POST/useManageExamInfo';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import * as AppPics from 'assets/images';

const { AppTextField, AppSelectBox, AppButton } = require('components');

const EMPTY = { name: '', status: 'active' };

export default function ExamTypeList() {
  const { boardId } = useParams();
  const history = useHistory();

  const { data: boardsData } = useGetExamBoards({ enabled: true });
  const boards = boardsData?.data || [];
  const board = boards.find((b) => b._id === boardId);

  const { data, isLoading } = useGetExamTypes({ boardId, enabled: !!boardId });
  const examTypes = data?.data || [];

  const [form, setForm] = useState({ ...EMPTY });
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' });
  const [examTypesList, setExamTypesList] = useState([]);

  const { mutate: save, isLoading: saving } = useManageExamType();
  const { mutate: del } = useDeleteExamType();
  const { mutate: reorderExamTypes, isLoading: isReordering } = useReorderExamTypes();

  useEffect(() => {
    setExamTypesList(examTypes.map((et) => ({ ...et, id: et._id })));
  }, [data]);

  const handleSortEnd = (newState) => {
    setExamTypesList(newState);

    const currentOrder = newState.map((et) => et.id);
    const previousOrder = examTypesList.map((et) => et.id);
    const hasOrderChanged = !currentOrder.every((id, index) => id === previousOrder[index]);
    if (!hasOrderChanged) return;

    const rows = newState.map((et, index) => ({ id: et.id, sortPosition: index + 1 }));
    reorderExamTypes(
      { boardId, rows },
      {
        onSuccess: () => notifier.success('Exam type order updated'),
        onError: () => {
          notifier.error('Failed to update order');
          setExamTypesList(examTypes.map((et) => ({ ...et, id: et._id })));
        },
      }
    );
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((prev) => ({ ...prev, name }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      notifier.error('Exam type name is required');
      return;
    }
    save(
      { boardId, ...form },
      {
        onSuccess: (res) => {
          notifier.success('Exam type created');
          const newId = res?.data?.data?._id || res?.data?._id;
          setForm({ ...EMPTY });
          setShowForm(false);
          if (newId) {
            history.push(`/podcast/exam-info/${boardId}/exam-types/${newId}`);
          }
        },
        onError: () => notifier.error('Failed to create exam type'),
      }
    );
  };

  const handleDeleteConfirm = () => {
    del(
      { examTypeId: deleteModal.id, boardId },
      {
        onSuccess: () => notifier.success('Deleted'),
        onError: () => notifier.error('Delete failed'),
      }
    );
    setDeleteModal({ open: false, id: null, name: '' });
  };

  return (
    <>
      <BasicDelete
        open={deleteModal.open}
        closeModal={() => setDeleteModal({ open: false, id: null, name: '' })}
        Header="Delete Exam Type"
        Body={`Are you sure you want to delete "${deleteModal.name}"?`}
        handleConfirmation={handleDeleteConfirm}
      />

      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={() => history.push('/podcast/exam-info')}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div className="tab__content ___adjust___tab___padding">
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast', to: '/podcast' },
                  { label: 'Exam Info', to: '/podcast/exam-info' },
                ]}
                current={board?.name || 'Exam Types'}
              />
            </div>

            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">
                        {board?.name ? `${board.name} — Exam Types` : 'Exam Types'}
                      </h2>
                    </li>
                  </ul>
                  {!showForm && (
                    <div className="flex items-center gap-10">
                      <button
                        className="btn-left-content add"
                        style={{ color: '#fff' }}
                        onClick={() => setShowForm(true)}
                      >
                        Add Exam Type
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Add exam type form (toggle) ───────────────────────────── */}
                {showForm && (
                  <div className="tab__contentBody mb-4">
                    <div
                      style={{
                        background: '#fafafa',
                        border: '1px solid #e8e8e8',
                        borderRadius: 8,
                        padding: 20,
                        maxWidth: 600,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 style={{ margin: 0, fontWeight: 600 }}>Add New Exam Type</h4>
                        <button
                          className="btn btn-sm btn-default"
                          onClick={() => {
                            setShowForm(false);
                            setForm({ ...EMPTY });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
                        After creating, you'll be taken to the detail page to configure dates,
                        marks, and syllabus.
                      </p>

                      <div className="row">
                        <div className="col-md-6">
                          <AppTextField
                            labelProps={{ label: 'Exam Type Name *' }}
                            inputProps={{
                              placeholder: 'e.g. Lower Division Clerk',
                              value: form.name,
                              onChange: handleNameChange,
                            }}
                          />
                        </div>
                        <div className="col-md-6">
                          <AppSelectBox
                            labelProps={{ label: 'Status' }}
                            selectProps={{
                              value: form.status,
                              onChange: (e) => setForm({ ...form, status: e.target.value }),
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="coming_soon">Coming Soon</option>
                          </AppSelectBox>
                        </div>
                      </div>

                      {/* <div className="row mt-3">
                        <div className="col-md-6">
                          <AppSelectBox
                            labelProps={{ label: 'Status' }}
                            selectProps={{
                              value: form.status,
                              onChange: (e) => setForm({ ...form, status: e.target.value }),
                            }}
                          >
                            <option value="active">Active</option>
                            <option value="coming_soon">Coming Soon</option>
                          </AppSelectBox>
                        </div>
                      </div> */}

                      <div className="mt-4">
                        <AppButton
                          variantClass="btn-primary"
                          onClick={handleSubmit}
                          disabled={saving}
                        >
                          {saving ? 'Creating…' : 'Create & Configure'}
                        </AppButton>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Exam types table ─────────────────────────────────────── */}
                <div className="tab__contentBody">
                  <div className="tableWrap">
                    <div className="table-responsive">
                      <table className="table tableWrap__table">
                        <thead className="thead-light __appTheadColor">
                          <tr>
                            <th className="text-center">#</th>
                            <th>Exam Type</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        {isLoading ? (
                          <tbody>
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                Loading exam types…
                              </td>
                            </tr>
                          </tbody>
                        ) : examTypesList.length === 0 ? (
                          <tbody>
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                No exam types yet. Add one above.
                              </td>
                            </tr>
                          </tbody>
                        ) : (
                          <ReactSortable
                            tag="tbody"
                            list={examTypesList}
                            setList={handleSortEnd}
                            handle=".examTypeGrabHandle"
                            animation={200}
                            disabled={isReordering}
                          >
                            {examTypesList.map((et, idx) => (
                              <tr key={et.id}>
                                <td className="text-center">{idx + 1}</td>
                                <td>{et.name}</td>
                                <td className="text-center">
                                  <span
                                    className={`badge ${
                                      et.status === 'active' ? 'badge-success' : 'badge-warning'
                                    }`}
                                    style={{ textTransform: 'capitalize' }}
                                  >
                                    {et.status.replace('_', ' ')}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span
                                    className="examTypeGrabHandle actions-icons mx-2"
                                    title="Drag to reorder"
                                    style={{
                                      display: 'inline-flex',
                                      cursor: isReordering ? 'wait' : 'grab',
                                    }}
                                  >
                                    <svg
                                      width="10"
                                      height="16"
                                      viewBox="0 0 10 16"
                                      fill="#9a9a9a"
                                      style={{ flexShrink: 0 }}
                                    >
                                      <circle cx="2" cy="2" r="1.5" />
                                      <circle cx="8" cy="2" r="1.5" />
                                      <circle cx="2" cy="8" r="1.5" />
                                      <circle cx="8" cy="8" r="1.5" />
                                      <circle cx="2" cy="14" r="1.5" />
                                      <circle cx="8" cy="14" r="1.5" />
                                    </svg>
                                  </span>
                                  <Link
                                    className="actions-icons mx-2 text-primary"
                                    title="Edit"
                                    to={`/podcast/exam-info/${boardId}/exam-types/${et._id}`}
                                  >
                                    <img src={AppPics.EditSvg} alt="Edit" />
                                  </Link>
                                  <button
                                    className="actions-icons mx-2 text-danger"
                                    title="Delete"
                                    onClick={() =>
                                      setDeleteModal({ open: true, id: et._id, name: et.name })
                                    }
                                  >
                                    <img src={AppPics.DeleteSvg} alt="Delete" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </ReactSortable>
                        )}
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
